import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Badge, Button } from './Common';
import AgentScanLaunchModal from './AgentScanLaunchModal';

const severityColors = {
  Critical: { bg: '#fef2f2', color: '#dc2626', bar: '#dc2626' },
  High: { bg: '#fff7ed', color: '#ea580c', bar: '#ea580c' },
  Medium: { bg: '#fffbeb', color: '#d97706', bar: '#f59e0b' },
  Low: { bg: '#fefce8', color: '#ca8a04', bar: '#eab308' },
  Info: { bg: '#eff6ff', color: '#2563eb', bar: '#3b82f6' },
};

export default function EngagementDrawer({ engagement, onClose }) {
  const { apiFetch, user } = useAuth();
  const navigate = useNavigate();
  const [findings, setFindings] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [launchAgent, setLaunchAgent] = useState(false);

  // Same gate as the /agent-scanner route. A finished engagement is not worth
  // scanning: its findings would land against a report that has already gone out.
  const canScan = user?.aiEnabled
    && engagement?.status !== 'Complete' && engagement?.status !== 'Cancelled';

  useEffect(() => {
    if (engagement) {
      requestAnimationFrame(() => setVisible(true));
      setLoading(true);
      Promise.all([
        apiFetch(`/findings/engagement/${engagement.id}`).then(r => r.json()).catch(() => []),
        apiFetch(`/engagements/${engagement.id}/notes`).then(r => r.json()).catch(() => []),
      ]).then(([f, n]) => {
        setFindings(Array.isArray(f) ? f : []);
        setNotes(Array.isArray(n) ? n : []);
        setLoading(false);
      });
    }
  }, [engagement, apiFetch]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      const res = await apiFetch(`/engagements/${engagement.id}/notes`, {
        method: 'POST', body: JSON.stringify({ content: newNote.trim() })
      });
      if (res.ok) {
        const note = await res.json();
        setNotes(prev => [note, ...prev]);
        setNewNote('');
      }
    } catch {}
    setAddingNote(false);
  };

  if (!engagement) return null;

  const severityCounts = {};
  findings.forEach(f => {
    const s = f.severity || 'Info';
    severityCounts[s] = (severityCounts[s] || 0) + 1;
  });
  const totalFindings = findings.length;
  const maxCount = Math.max(...Object.values(severityCounts), 1);

  const testerInitials = engagement.tester_name
    ? engagement.tester_name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()
    : '';
  const avatarColors = ['#3b82f6','#8b5cf6','#06b6d4','#f59e0b','#ec4899','#10b981'];
  const avatarColor = engagement.tester_name
    ? avatarColors[engagement.tester_name.charCodeAt(0) % avatarColors.length]
    : '#94a3b8';

  return (
    <>
      {/* Overlay */}
      <div onClick={handleClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', zIndex: 200,
        opacity: visible ? 1 : 0, transition: 'opacity 0.2s',
      }} />

      {/* Drawer */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '480px', maxWidth: '90vw',
        background: 'var(--bg-card)', borderLeft: '1px solid var(--border)',
        boxShadow: '-8px 0 24px rgba(0,0,0,0.08)', zIndex: 201,
        transform: visible ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.2s ease-out',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: 12, flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 500, marginBottom: 4 }}>{engagement.client_name}</div>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.3px', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {engagement.name || engagement.engagement_type}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <Badge>{engagement.status}</Badge>
              {engagement.engagement_type && (
                <span style={{ fontSize: 11, color: 'var(--text-secondary)', background: 'var(--bg-input)', padding: '2px 8px', borderRadius: 10 }}>{engagement.engagement_type}</span>
              )}
            </div>
          </div>
          <button onClick={handleClose} style={{
            background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer',
            fontSize: 20, lineHeight: 1, padding: '4px', borderRadius: 6,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {/* Metadata grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', marginBottom: 20 }}>
            <MetaItem label="Tester" value={
              engagement.tester_name ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: avatarColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700, color: 'white' }}>{testerInitials}</div>
                  <span>{engagement.tester_name}</span>
                </div>
              ) : 'Unassigned'
            } />
            <MetaItem label="SoW" value={engagement.sow_number || '—'} />
            {engagement.third_party && <MetaItem label="Vendor" value={engagement.vendor_name || 'TSR Solutions'} />}
            <MetaItem label="Start Date" value={engagement.start_date ? new Date(engagement.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'} />
            <MetaItem label="End Date" value={engagement.end_date ? new Date(engagement.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'} />
            <MetaItem label="Environment" value={engagement.environment_type || '—'} />
            <MetaItem label="Scope Items" value={engagement.scope_count || 0} />
          </div>

          {/* Findings summary */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
              Findings ({totalFindings})
            </div>
            {loading ? (
              <div style={{ display: 'flex', gap: 6 }}>
                {[1,2,3,4,5].map(i => <div key={i} style={{ height: 40, flex: 1, background: 'var(--bg-input)', borderRadius: 6, animation: 'shimmer 1.5s infinite', backgroundSize: '200% 100%', backgroundImage: 'linear-gradient(90deg, var(--bg-input) 25%, var(--border) 50%, var(--bg-input) 75%)' }} />)}
              </div>
            ) : totalFindings === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '12px 0' }}>No findings yet</div>
            ) : (
              <div style={{ display: 'flex', gap: 6 }}>
                {['Critical', 'High', 'Medium', 'Low', 'Info'].map(sev => {
                  const count = severityCounts[sev] || 0;
                  const sc = severityColors[sev];
                  return (
                    <div key={sev} style={{ flex: 1, textAlign: 'center' }}>
                      <div style={{
                        height: Math.max(8, (count / maxCount) * 48), background: sc.bar,
                        borderRadius: '4px 4px 0 0', opacity: count > 0 ? 1 : 0.15,
                        transition: 'height 0.3s', marginBottom: 4, minHeight: 8,
                      }} />
                      <div style={{ fontSize: 14, fontWeight: 700, color: count > 0 ? sc.color : 'var(--text-muted, #94a3b8)' }}>{count}</div>
                      <div style={{ fontSize: 9, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.3px', marginTop: 1 }}>{sev}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Recent Notes */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
              Notes
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              <input value={newNote} onChange={e => setNewNote(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addNote(); } }}
                placeholder="Add a note..."
                style={{
                  flex: 1, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8,
                  fontSize: 12, background: 'var(--bg-input)', color: 'var(--text)', outline: 'none',
                  fontFamily: 'inherit',
                }} />
              <button onClick={addNote} disabled={addingNote || !newNote.trim()}
                style={{
                  padding: '8px 14px', borderRadius: 8, border: 'none', background: '#3b82f6',
                  color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  opacity: addingNote || !newNote.trim() ? 0.5 : 1, fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
                }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add
              </button>
            </div>
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[1,2].map(i => <div key={i} style={{ height: 48, background: 'var(--bg-input)', borderRadius: 8 }} />)}
              </div>
            ) : notes.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '8px 0' }}>No notes</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {notes.slice(0, 4).map(note => (
                  <div key={note.id} style={{
                    padding: '10px 12px', background: 'var(--bg-input)', borderRadius: 8,
                    fontSize: 12, lineHeight: 1.5, color: 'var(--text)',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontWeight: 600, fontSize: 11 }}>{note.author_name || 'Unknown'}</span>
                      <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>
                        {new Date(note.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                    <div style={{ color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {note.content}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div style={{
          padding: '14px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8,
          flexShrink: 0, background: 'var(--bg-card)', flexWrap: 'wrap',
        }}>
          {canScan && (
            <Button onClick={() => setLaunchAgent(true)}
              style={{
                flexBasis: '100%', justifyContent: 'center', fontSize: 13, fontWeight: 600,
                padding: '10px 16px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              }}>
              <span style={{ fontSize: 14 }}>&#129302;</span>
              Send to AI Pentest Agent
            </Button>
          )}
          <Button onClick={() => navigate(`/engagements/${engagement.id}`)}
            style={{ flex: 1, justifyContent: 'center', fontSize: 13, fontWeight: 600, padding: '10px 16px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            Open Full
          </Button>
          <Button variant="secondary" onClick={handleClose}
            style={{ fontSize: 13, padding: '10px 16px' }}>
            Close
          </Button>
        </div>
      </div>

      {/* Sits above the drawer (z-index 1000 vs 201), so the engagement stays
          visible behind the scan configuration. */}
      {launchAgent && (
        <AgentScanLaunchModal engagement={engagement} onClose={() => setLaunchAgent(false)} />
      )}

      <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
    </>
  );
}

function MetaItem({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted, #94a3b8)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, color: 'var(--text)' }}>{value}</div>
    </div>
  );
}
